import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getEnhancedAdminStats, EnhancedAdminStats } from '@/utils/enhanced-admin-helpers';
import { glassCard, glassCardStatic as glassCardLg } from '@/utils/admin-glass-styles';
import { 
  Users, 
  UserCog, 
  Calendar, 
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Target,
  CreditCard,
  Wallet,
  PiggyBank,
  BarChart3,
  Share2
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { CustomBadge } from '@/components/ui/custom-badge';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<EnhancedAdminStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [financialOverview, setFinancialOverview] = useState<any>(null);
  const [financialLoading, setFinancialLoading] = useState<boolean>(true);
  const [referralStats, setReferralStats] = useState<any>(null);
  const [referralLoading, setReferralLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const dashboardStats = await getEnhancedAdminStats();
        setStats(dashboardStats);
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchFinancialOverview = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-admin-financial-overview');
        if (error) {
          console.error('Error fetching financial overview:', error);
        } else {
          setFinancialOverview(data);
        }
      } catch (error) {
        console.error('Error fetching financial overview:', error);
      } finally {
        setFinancialLoading(false);
      }
    };

    const fetchReferralStats = async () => {
      try {
        // Get referred counts
        const { count: referredClients } = await supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .like('referral_source', 'REFERRAL:%');

        const { count: referredPractitioners } = await supabase
          .from('practitioners')
          .select('*', { count: 'exact', head: true })
          .like('referral_source', 'REFERRAL:%');

        const { count: clientsWithCodes } = await supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .not('referral_code', 'is', null);

        const { count: practitionersWithCodes } = await supabase
          .from('practitioners')
          .select('*', { count: 'exact', head: true })
          .not('referral_code', 'is', null);

        // Get top referrers from clients
        const { data: clientReferrers } = await supabase
          .from('clients')
          .select('id, first_name, last_name, referral_code')
          .not('referral_code', 'is', null);

        // Get top referrers from practitioners
        const { data: practitionerReferrers } = await supabase
          .from('practitioners')
          .select('id, first_name, last_name, referral_code')
          .not('referral_code', 'is', null);

        // For each referrer, count how many people they referred
        const topReferrers: any[] = [];
        
        const allReferrers = [
          ...(clientReferrers || []).map(r => ({ ...r, type: 'client' })),
          ...(practitionerReferrers || []).map(r => ({ ...r, type: 'practitioner' }))
        ];

        for (const referrer of allReferrers) {
          const pattern = `REFERRAL:%:${referrer.id}`;
          const { count: cCount } = await supabase
            .from('clients')
            .select('*', { count: 'exact', head: true })
            .like('referral_source', pattern);
          const { count: pCount } = await supabase
            .from('practitioners')
            .select('*', { count: 'exact', head: true })
            .like('referral_source', pattern);
          const total = (cCount || 0) + (pCount || 0);
          if (total > 0) {
            topReferrers.push({
              id: referrer.id,
              name: `${referrer.first_name || ''} ${referrer.last_name || ''}`.trim() || 'Unknown',
              code: referrer.referral_code,
              type: referrer.type,
              referredClients: cCount || 0,
              referredPractitioners: pCount || 0,
              total
            });
          }
        }

        topReferrers.sort((a, b) => b.total - a.total);

        setReferralStats({
          referredClients: referredClients || 0,
          referredPractitioners: referredPractitioners || 0,
          totalReferred: (referredClients || 0) + (referredPractitioners || 0),
          codesIssued: (clientsWithCodes || 0) + (practitionersWithCodes || 0),
          topReferrers: topReferrers.slice(0, 10)
        });
      } catch (error) {
        console.error('Error fetching referral stats:', error);
      } finally {
        setReferralLoading(false);
      }
    };

    fetchStats();
    fetchFinancialOverview();
    fetchReferralStats();
  }, []);

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Glass styles imported from shared constants

  const renderStatCard = (title: string, value: number | string, icon: React.ReactNode, description?: string, growth?: number) => (
    <Card className={cn("h-full", glassCard)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground/70 truncate">{title}</CardTitle>
        <div className="flex-shrink-0 p-2 rounded-xl bg-white/10">{icon}</div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          {isLoading ? <Skeleton className="h-7 sm:h-9 w-16 sm:w-24" /> : value}
        </div>
        {description && <p className="text-xs text-muted-foreground/60">{description}</p>}
        {growth !== undefined && !isLoading && (
          <p className="text-xs text-success flex items-center">
            <TrendingUp className="h-3 w-3 mr-1" />
            +{growth} this month
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Admin Dashboard</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Overview of platform performance and key metrics
        </p>
      </div>
      
      {/* Key Metrics */}
      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {renderStatCard(
          "Total Clients", 
          stats?.clientCount || 0, 
          <Users className="h-4 w-4 text-gray-500" />,
          undefined,
          stats?.monthlyGrowth.clients
        )}
        {renderStatCard(
          "Total Practitioners", 
          stats?.practitionerCount || 0, 
          <UserCog className="h-4 w-4 text-gray-500" />,
          undefined,
          stats?.monthlyGrowth.practitioners
        )}
        {renderStatCard(
          "Pending", 
          stats?.pendingPractitionerCount || 0, 
          <AlertTriangle className="h-4 w-4 text-amber-500" />
        )}
        {renderStatCard(
          "Total Bookings", 
          stats?.bookingCount || 0, 
          <Calendar className="h-4 w-4 text-gray-500" />,
          undefined,
          stats?.monthlyGrowth.bookings
        )}
      </div>

      {/* Financial Overview */}
      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {renderStatCard(
          "Total Platform Revenue", 
          formatCurrency(financialOverview?.totalPlatformRevenue || 0), 
          <DollarSign className="h-4 w-4 text-green-500" />,
          "All-time platform revenue"
        )}
        {renderStatCard(
          "Monthly Revenue", 
          formatCurrency(financialOverview?.monthlyRevenue || 0), 
          <TrendingUp className="h-4 w-4 text-blue-500" />,
          "This month's revenue"
        )}
        {renderStatCard(
          "Platform Commission", 
          formatCurrency(financialOverview?.totalPlatformCommission || 0), 
          <CreditCard className="h-4 w-4 text-purple-500" />,
          `${financialOverview?.platformCommissionRate || 0}% commission`
        )}
        {renderStatCard(
          "Practitioner Earnings", 
          formatCurrency(financialOverview?.totalPractitionerEarnings || 0), 
          <Wallet className="h-4 w-4 text-amber-500" />,
          "Total paid to practitioners"
        )}
      </div>

      {/* Performance Metrics */}
      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 sm:grid-cols-3">
        {renderStatCard(
          "Conversion Rate", 
          `${stats?.conversionRate || 0}%`, 
          <Target className="h-4 w-4 text-blue-500" />,
          "Clients who made bookings"
        )}
        {renderStatCard(
          "Avg Booking Value", 
          formatCurrency(financialOverview?.averageBookingValue || stats?.avgBookingValue || 0), 
          <DollarSign className="h-4 w-4 text-green-500" />,
          "Average revenue per booking"
        )}
        {renderStatCard(
          "Weekly Growth", 
          `+${stats?.weeklyGrowth.clients || 0}`, 
          <TrendingUp className="h-4 w-4 text-purple-500" />,
          "New clients this week"
        )}
      </div>

      {/* Top Performing Practitioners */}
      <Card className={glassCardLg}>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Top Performing Practitioners</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Name</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Bookings</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.topPerformingPractitioners?.length ? (
                    stats.topPerformingPractitioners.map((practitioner) => (
                      <TableRow key={practitioner.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">{practitioner.name}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{practitioner.totalBookings}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{formatCurrency(practitioner.totalRevenue)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-xs sm:text-sm">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-2 sm:gap-3 lg:gap-4 grid-cols-1 lg:grid-cols-2">
        <Card className={glassCardLg}>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="text-sm sm:text-base lg:text-lg">Recently Joined Clients</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6">
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <Table>
                   <TableHeader>
                     <TableRow>
                       <TableHead className="text-xs sm:text-sm whitespace-nowrap">Name</TableHead>
                       <TableHead className="text-xs sm:text-sm whitespace-nowrap">Email</TableHead>
                       <TableHead className="text-xs sm:text-sm whitespace-nowrap">Registration Date</TableHead>
                     </TableRow>
                   </TableHeader>
                  <TableBody>
                    {stats?.recentClients?.length ? (
                      stats.recentClients.map((client: any) => (
                        <TableRow key={client.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {`${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Not Set'}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">{client.email || 'Not Set'}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{formatDateTime(client.created_at)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-xs sm:text-sm">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={glassCardLg}>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Recently Joined Practitioners</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Name</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Status</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Registration Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.recentPractitioners?.length ? (
                      stats.recentPractitioners.map((practitioner: any) => (
                        <TableRow key={practitioner.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {`${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim() || 'Not Set'}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            <CustomBadge variant={
                              practitioner.profile_status === 'pending_review' ? "outline" :
                              practitioner.profile_status === 'approved' ? "success" : "destructive"
                            }>
                              {
                                practitioner.profile_status === 'pending_review' ? 'Pending Review' :
                                practitioner.profile_status === 'approved' ? 'Approved' : 
                                practitioner.profile_status === 'rejected' ? 'Rejected' : practitioner.profile_status
                              }
                            </CustomBadge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">{formatDateTime(practitioner.created_at)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-xs sm:text-sm">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Statistics */}
      <Card className={glassCardLg}>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Share2 className="h-5 w-5 text-muted-foreground" />
            Referral Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 mb-6">
            <div className="rounded-2xl border border-white/20 bg-white/[0.08] backdrop-blur-[20px] p-3 text-center">
              <p className="text-2xl font-bold">{referralLoading ? '—' : referralStats?.totalReferred || 0}</p>
              <p className="text-xs text-muted-foreground">Total Referred</p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/[0.08] backdrop-blur-[20px] p-3 text-center">
              <p className="text-2xl font-bold">{referralLoading ? '—' : referralStats?.referredClients || 0}</p>
              <p className="text-xs text-muted-foreground">Referred Clients</p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/[0.08] backdrop-blur-[20px] p-3 text-center">
              <p className="text-2xl font-bold">{referralLoading ? '—' : referralStats?.referredPractitioners || 0}</p>
              <p className="text-xs text-muted-foreground">Referred Practitioners</p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/[0.08] backdrop-blur-[20px] p-3 text-center">
              <p className="text-2xl font-bold">{referralLoading ? '—' : referralStats?.codesIssued || 0}</p>
              <p className="text-xs text-muted-foreground">Codes Issued</p>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-3">Top Referrers</h4>
          <div className="overflow-x-auto">
            {referralLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm">Name</TableHead>
                    <TableHead className="text-xs sm:text-sm">Code</TableHead>
                    <TableHead className="text-xs sm:text-sm">Type</TableHead>
                    <TableHead className="text-xs sm:text-sm">Clients</TableHead>
                    <TableHead className="text-xs sm:text-sm">Practitioners</TableHead>
                    <TableHead className="text-xs sm:text-sm">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralStats?.topReferrers?.length ? (
                    referralStats.topReferrers.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">{r.name}</TableCell>
                        <TableCell className="text-xs sm:text-sm font-mono">{r.code}</TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <CustomBadge variant={r.type === 'practitioner' ? 'outline' : 'secondary'}>
                            {r.type}
                          </CustomBadge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{r.referredClients}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{r.referredPractitioners}</TableCell>
                        <TableCell className="text-xs sm:text-sm font-bold">{r.total}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-xs sm:text-sm text-muted-foreground">
                        No referral data yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className={glassCardLg}>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Recent Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Client</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Practitioner</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Start Time</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Status</TableHead>
                    <TableHead className="text-xs sm:text-sm whitespace-nowrap">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.recentBookings?.length ? (
                    stats.recentBookings.map((booking: any) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">
                          {`${booking.clients?.first_name || ''} ${booking.clients?.last_name || ''}`.trim() || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {`${booking.practitioners?.first_name || ''} ${booking.practitioners?.last_name || ''}`.trim() || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <div className="flex items-center">
                            <Clock className="mr-1 h-3 w-3" />
                            {formatDateTime(booking.start_time)}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <CustomBadge variant={
                            booking.status === 'Scheduled' ? "outline" :
                            booking.status === 'Completed' ? "success" :
                            booking.status === 'Cancelled' ? "destructive" : "secondary"
                          }>
                            {booking.status}
                          </CustomBadge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{formatCurrency(parseFloat(booking.price_at_booking || 0))} {booking.currency_at_booking}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-xs sm:text-sm">No booking data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
